// Frontend types for VC Onboarding Flow

export type FundSize =
  | "under_10m"
  | "10m_25m"
  | "25m_50m"
  | "50m_100m"
  | "100m_250m"
  | "250m_500m"
  | "500m_1b"
  | "over_1b";

export const FUND_SIZE_LABELS: Record<FundSize, string> = {
  under_10m: "Under $10M",
  "10m_25m": "$10M - $25M",
  "25m_50m": "$25M - $50M",
  "50m_100m": "$50M - $100M",
  "100m_250m": "$100M - $250M",
  "250m_500m": "$250M - $500M",
  "500m_1b": "$500M - $1B",
  over_1b: "Over $1B",
};

export type InvestmentStage =
  | "pre_seed"
  | "seed"
  | "series_a"
  | "series_b"
  | "series_c_plus"
  | "growth";

export const INVESTMENT_STAGE_LABELS: Record<InvestmentStage, string> = {
  pre_seed: "Pre-Seed",
  seed: "Seed",
  series_a: "Series A",
  series_b: "Series B",
  series_c_plus: "Series C+",
  growth: "Growth",
};

export type InvestmentSector =
  | "ai_ml"
  | "fintech"
  | "healthtech"
  | "cleantech"
  | "saas"
  | "consumer"
  | "enterprise"
  | "crypto_web3"
  | "biotech"
  | "hardware";

export const INVESTMENT_SECTOR_LABELS: Record<InvestmentSector, string> = {
  ai_ml: "AI/ML",
  fintech: "FinTech",
  healthtech: "HealthTech",
  cleantech: "CleanTech",
  saas: "SaaS",
  consumer: "Consumer",
  enterprise: "Enterprise",
  crypto_web3: "Crypto/Web3",
  biotech: "Biotech",
  hardware: "Hardware",
};

export type IntegrationProvider =
  | "mercury"
  | "brex"
  | "ramp"
  | "quickbooks"
  | "xero"
  | "svb"
  | "stripe"
  | "chargebee"
  | "paddle"
  | "recurly"
  | "plaid"
  | "github"
  | "gitlab"
  | "linear"
  | "jira"
  | "notion"
  | "posthog"
  | "amplitude"
  | "mixpanel"
  | "segment"
  | "heap"
  | "hubspot"
  | "salesforce"
  | "pipedrive"
  | "close"
  | "attio"
  | "slack"
  | "intercom"
  | "zendesk"
  | "google-calendar"
  | "google-drive"
  | "gmail"
  | "mailchimp"
  | "trello"
  | "asana"
  | "calendly"
  | "airtable";

// Integration category type
export type IntegrationCategory =
  | "banking"
  | "revenue"
  | "engineering"
  | "product_analytics"
  | "crm_sales"
  | "communication"
  | "productivity";

// Category display information for VC onboarding
export interface IntegrationCategoryInfo {
  id: IntegrationCategory;
  title: string;
  description: string;
  icon: string;
}

export const INTEGRATION_CATEGORIES: IntegrationCategoryInfo[] = [
  {
    id: "banking",
    title: "Banking & Revenue",
    description: "Plaid, Stripe",
    icon: "💰",
  },
  {
    id: "engineering",
    title: "Engineering & Build",
    description: "GitHub, GitLab, Linear, Jira, Notion",
    icon: "⚡",
  },
  {
    id: "product_analytics",
    title: "Product Analytics",
    description: "PostHog, Amplitude, Mixpanel, Segment, Heap",
    icon: "📊",
  },
  {
    id: "crm_sales",
    title: "CRM & Sales",
    description: "HubSpot, Salesforce, Pipedrive, Close, Attio",
    icon: "🤝",
  },
  {
    id: "communication",
    title: "Communication",
    description: "Slack, Intercom, Zendesk, Gmail",
    icon: "💬",
  },
  {
    id: "productivity",
    title: "Productivity",
    description: "Google Calendar, Google Drive, Calendly, Airtable",
    icon: "📅",
  },
];

export interface IntegrationInfo {
  id: IntegrationProvider;
  name: string;
  category: IntegrationCategory;
  icon?: string;
  logo?: string;
  domain?: string;
}

export const INTEGRATIONS: IntegrationInfo[] = [
  {
    id: "mercury",
    name: "Mercury",
    category: "banking",
    domain: "mercury.com",
    logo: "/assets/integrations/mercury.svg",
  },
  {
    id: "brex",
    name: "Brex",
    category: "banking",
    domain: "brex.com",
  },
  {
    id: "ramp",
    name: "Ramp",
    category: "banking",
    domain: "ramp.com",
    logo: "/assets/integrations/ramp.svg",
  },
  {
    id: "quickbooks",
    name: "QuickBooks",
    category: "banking",
    domain: "quickbooks.com",
  },
  {
    id: "xero",
    name: "Xero",
    category: "banking",
    domain: "xero.com",
  },
  {
    id: "svb",
    name: "SVB",
    category: "banking",
    domain: "svb.com",
    logo: "/assets/integrations/svb.jpeg",
  },
  {
    id: "stripe",
    name: "Stripe",
    category: "banking",
    domain: "stripe.com",
  },
  {
    id: "chargebee",
    name: "Chargebee",
    category: "revenue",
    domain: "chargebee.com",
    logo: "/assets/integrations/chargebee.svg",
  },
  {
    id: "paddle",
    name: "Paddle",
    category: "revenue",
    domain: "paddle.com",
  },
  {
    id: "recurly",
    name: "Recurly",
    category: "revenue",
    domain: "recurly.com",
    logo: "/assets/integrations/recurly.jpeg",
  },
  {
    id: "plaid",
    name: "Plaid",
    category: "banking",
    domain: "plaid.com",
    logo: "/assets/integrations/plaid.jpeg",
  },
  {
    id: "github",
    name: "GitHub",
    category: "engineering",
    domain: "github.com",
  },
  {
    id: "gitlab",
    name: "GitLab",
    category: "engineering",
    domain: "gitlab.com",
  },
  {
    id: "linear",
    name: "Linear",
    category: "engineering",
    domain: "linear.app",
  },
  {
    id: "jira",
    name: "Jira",
    category: "engineering",
    domain: "atlassian.com",
  },
  {
    id: "notion",
    name: "Notion",
    category: "engineering",
    domain: "notion.so",
  },
  {
    id: "posthog",
    name: "PostHog",
    category: "product_analytics",
    domain: "posthog.com",
  },
  {
    id: "amplitude",
    name: "Amplitude",
    category: "product_analytics",
    domain: "amplitude.com",
    logo: "/assets/integrations/amplitude.svg",
  },
  {
    id: "mixpanel",
    name: "Mixpanel",
    category: "product_analytics",
    domain: "mixpanel.com",
  },
  {
    id: "segment",
    name: "Segment",
    category: "product_analytics",
    domain: "segment.com",
    logo: "/assets/integrations/segment.png",
  },
  {
    id: "heap",
    name: "Heap",
    category: "product_analytics",
    domain: "heap.io",
    logo: "/assets/integrations/heap-white.svg",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    category: "crm_sales",
    domain: "hubspot.com",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    category: "crm_sales",
    domain: "salesforce.com",
  },
  {
    id: "pipedrive",
    name: "Pipedrive",
    category: "crm_sales",
    domain: "pipedrive.com",
    logo: "/assets/integrations/pipedrive.svg",
  },
  {
    id: "close",
    name: "Close",
    category: "crm_sales",
    domain: "close.com",
    logo: "/assets/integrations/close.jpeg",
  },
  {
    id: "attio",
    name: "Attio",
    category: "crm_sales",
    domain: "attio.com",
    logo: "/assets/integrations/attio.svg",
  },
  {
    id: "slack",
    name: "Slack",
    category: "communication",
    domain: "slack.com",
  },
  {
    id: "intercom",
    name: "Intercom",
    category: "communication",
    domain: "intercom.com",
  },
  {
    id: "zendesk",
    name: "Zendesk",
    category: "communication",
    domain: "zendesk.com",
  },
  {
    id: "google-calendar",
    name: "Google Calendar",
    category: "productivity",
    domain: "calendar.google.com",
    logo: "/assets/integrations/google-calendar.svg",
  },
  {
    id: "google-drive",
    name: "Google Drive",
    category: "productivity",
    domain: "drive.google.com",
    logo: "/assets/integrations/google-drive.svg",
  },
  {
    id: "gmail",
    name: "Gmail",
    category: "communication",
    domain: "gmail.com",
    logo: "/assets/integrations/gmail.svg",
  },
  {
    id: "mailchimp",
    name: "Mailchimp",
    category: "crm_sales",
    domain: "mailchimp.com",
    logo: "/assets/integrations/mailchimp.svg",
  },
  {
    id: "trello",
    name: "Trello",
    category: "engineering",
    domain: "trello.com",
    logo: "/assets/integrations/trello.svg",
  },
  {
    id: "asana",
    name: "Asana",
    category: "engineering",
    domain: "asana.com",
    logo: "/assets/integrations/asana.svg",
  },
  {
    id: "calendly",
    name: "Calendly",
    category: "productivity",
    domain: "calendly.com",
    logo: "/assets/integrations/calendly.svg",
  },
  {
    id: "airtable",
    name: "Airtable",
    category: "productivity",
    domain: "airtable.com",
    logo: "/assets/integrations/airtable.svg",
  },
];

export type BusinessModel = "b2b" | "b2c" | "b2b2c" | "marketplace";

export const BUSINESS_MODEL_LABELS: Record<BusinessModel, string> = {
  b2b: "B2B",
  b2c: "B2C",
  b2b2c: "B2B2C",
  marketplace: "Marketplace",
};

export type KeyMetric =
  | "mrr_arr"
  | "revenue_growth"
  | "burn_rate"
  | "build_velocity"
  | "customer_count"
  | "churn_rate"
  | "nps_score"
  | "cac_ltv"
  | "dau_mau"
  | "gmv";

export const KEY_METRIC_LABELS: Record<KeyMetric, string> = {
  mrr_arr: "MRR / ARR",
  revenue_growth: "Revenue Growth",
  burn_rate: "Burn Rate",
  build_velocity: "Build Velocity",
  customer_count: "Customer Count",
  churn_rate: "Churn Rate",
  nps_score: "NPS Score",
  cac_ltv: "CAC / LTV",
  dau_mau: "DAU / MAU",
  gmv: "GMV",
};

export const KEY_METRIC_DESCRIPTIONS: Record<KeyMetric, string> = {
  mrr_arr: "Monthly/Annual recurring revenue",
  revenue_growth: "Month-over-month growth rate",
  burn_rate: "Monthly cash burn & runway",
  build_velocity: "Shipping speed & releases",
  customer_count: "Total & active customers",
  churn_rate: "Customer & revenue churn",
  nps_score: "Net promoter score",
  cac_ltv: "Unit economics",
  dau_mau: "Active user metrics",
  gmv: "Gross merchandise value",
};

export type UpdateFrequency = "weekly" | "bi_weekly" | "monthly" | "quarterly";

export const UPDATE_FREQUENCY_LABELS: Record<UpdateFrequency, string> = {
  weekly: "Weekly",
  bi_weekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
};

export const UPDATE_FREQUENCY_DESCRIPTIONS: Record<UpdateFrequency, string> = {
  weekly: "Get updates every week",
  bi_weekly: "Get updates every two weeks",
  monthly: "Get updates once a month",
  quarterly: "Get updates every quarter",
};

// Step 1: Fund Information
export interface VCFundInfo {
  fundName: string;
  fundSize: FundSize;
  website: string;
}

// Step 2: Investment Focus
export interface VCInvestmentFocus {
  preferredStages: InvestmentStage[];
  sectors: InvestmentSector[];
  portfolioCompanies: number;
}

// Step 3: Integrations (now category-based)
export interface VCIntegrations {
  selectedCategories: IntegrationCategory[];
}

// Step 4: Insights/Metrics
export interface VCInsights {
  businessModels: BusinessModel[];
  keyMetrics: KeyMetric[];
  customMetrics?: string;
}

// Step 5: Update Preferences
export interface VCPreferences {
  updateFrequency: UpdateFrequency;
}

// Complete VC Onboarding Data
export interface VCOnboardingData {
  step1_fund_info?: VCFundInfo;
  step2_investment_focus?: VCInvestmentFocus;
  step3_integrations?: VCIntegrations;
  step4_insights?: VCInsights;
  step5_preferences?: VCPreferences;
  completed: boolean;
  completedAt?: string;
  currentStep?: number;
}

// Context state
export interface VCOnboardingContextState {
  currentStep: number;
  data: VCOnboardingData;
  loading: boolean;
  error: string | null;
  saveStep: (
    step: number,
    stepData:
      | VCFundInfo
      | VCInvestmentFocus
      | VCIntegrations
      | VCInsights
      | VCPreferences,
  ) => Promise<void>;
  nextStep: () => void;
  previousStep: () => void;
  completeOnboarding: () => Promise<void>;
}
